import { useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TestingAccordion from "../TestingAccordion/TestingAccordion";
import { ICreateBalanceOrderItem } from "../../../../../interfaces/Order";
import TestFrequencyInputs from "../TestFrequencyInputs/TestFrequencyInputs";
import { Eyes, Feet, TestType } from "../../../../../enums/Test";
import BalanceParameterInput from "./BalanceParameterInput/BalanceParameterInput";
import {
  BalanceData,
  TestsData,
} from "../../../../../recoil-states/create-order-states";
import { getBalanceFeetOptions, getTestDurationOptions } from "../../../../../helpers/TestHelper";
import { getDefaultTestsData, extractDurationAndType } from "../../../../../helpers/OrderHelper";
import InputField from "../../../../InputField/InputField";
import { DurationType, FrequencyType } from "../../../../../enums/Order";
import { TestDurationValue } from "../../../../../interfaces/Test";
import styles from "./BalanceTest.module.scss";

const BalanceTest = () => {
  const { selected, duration, durationType, tests, frequency, frequencyReps } =
    useRecoilValue(BalanceData);
  const setTestsData = useSetRecoilState(TestsData);

  const [isCustomMode, setIsCustomMode] = useState<boolean>(false);

  const feetOptions = useMemo((): Feet[] => getBalanceFeetOptions(), []);

  const eyesNotSelected = useMemo(
    (): boolean => tests.some(({ params: { eyes } }) => !eyes),
    [tests]
  );

  const error = useMemo(
    (): boolean =>
      Boolean(selected && (eyesNotSelected || !frequencyReps || !tests.length || !duration)),
    [selected, eyesNotSelected, frequencyReps, tests.length, duration]
  );

  // Determine if duration is custom based on whether it matches preset options OR custom mode is active
  const isCustomDuration = useMemo(() => {
    if (isCustomMode) return true;
    if (!duration) return false;
    const predefinedOptions = getTestDurationOptions();
    // Check if the current duration+type combination matches any preset option
    return !predefinedOptions.some(({ value }) => {
      const [presetDuration, presetType] = value.split("_").map(Number);
      return duration === presetDuration && durationType === presetType;
    });
  }, [duration, durationType, isCustomMode]);

  // Get duration value for dropdown
  const durationDropdownValue = useMemo((): string => {
    if (isCustomMode || isCustomDuration) return "custom";
    if (!duration) return "";
    return `${duration}_${durationType}`;
  }, [duration, durationType, isCustomDuration, isCustomMode]);

  // Calculate minutes and seconds from total duration (in seconds)
  const customDurationInputs = useMemo(() => {
    if (!isCustomDuration || !duration) return { minutes: 0, seconds: 0 };

    const totalSeconds = duration;
    const seconds = totalSeconds % 60;
    const minutes = (totalSeconds - seconds) / 60;

    return { minutes, seconds };
  }, [isCustomDuration, duration]);

  const handleDataChange = useCallback(
    (value: Partial<ICreateBalanceOrderItem>) => {
      setTestsData((prev) => ({
        ...prev,
        [TestType.Balance]: {
          ...prev[TestType.Balance],
          ...value,
          selected: value.selected ?? true, // select test on input change
        },
      }));
    },
    [setTestsData]
  );

  useEffect(() => {
    setTestsData((prev) => ({
      ...prev,
      [TestType.Balance]: { ...prev[TestType.Balance], valid: !error },
    }));
  }, [error, setTestsData]);

  // Detect custom duration when loading from API
  useEffect(() => {
    if (duration && durationType) {
      const predefinedOptions = getTestDurationOptions();
      const isPreset = predefinedOptions.some(({ value }) => {
        const [presetDuration, presetType] = value.split("_").map(Number);
        return duration === presetDuration && durationType === presetType;
      });
      setIsCustomMode(!isPreset);
    }
  }, [duration, durationType]);

  const handleSelectionChange = (value: boolean): void => {
    if (value) {
      // Auto-select default: 10 seconds, 1 time per week, Feet Apart Eyes Open only
      setIsCustomMode(false); // Default is preset, not custom

      handleDataChange({
        selected: true,
        duration: 10,
        durationType: DurationType.Seconds,
        frequency: FrequencyType.Weekly,
        frequencyReps: 1,
        tests: [{ params: { feet: Feet.ParallelApart, eyes: Eyes.Open } }],
      });
    } else {
      setIsCustomMode(false);
      handleDataChange(getDefaultTestsData()[TestType.Balance]);
    }
  };

  const handleDurationDropdownChange = (value: string) => {
    if (value === "custom") {
      // Switch to custom duration mode
      setIsCustomMode(true);
      // Keep current duration or set to undefined to show error
      if (!duration) {
        handleDataChange({
          duration: undefined,
          durationType: DurationType.Seconds,
        });
      }
    } else {
      // Preset duration selected - exit custom mode
      setIsCustomMode(false);
      const { duration, durationType } = extractDurationAndType(value as TestDurationValue);
      handleDataChange({
        duration,
        durationType,
      });
    }
  };

  const handleCustomMinutesChange = (value: string) => {
    const minutes = Number(value) || 0;
    const seconds = customDurationInputs.seconds;
    const totalDuration = minutes * 60 + seconds;

    handleDataChange({
      duration: totalDuration !== 0 ? totalDuration : undefined,
      durationType: DurationType.Seconds,
    });
  };

  const handleCustomSecondsChange = (value: string) => {
    const seconds = Number(value) || 0;
    const minutes = customDurationInputs.minutes;
    const totalDuration = minutes * 60 + seconds;

    handleDataChange({
      duration: totalDuration !== 0 ? totalDuration : undefined,
      durationType: DurationType.Seconds,
    });
  };

  const showDurationError = selected && isCustomDuration && !duration;

  return (
    <TestingAccordion
      error={error}
      testName="Balance"
      selected={selected}
      onSelectionChange={handleSelectionChange}
      children={
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap gap-5 items-start">
            {/* Duration Column */}
            <div className="flex flex-col gap-2 w-full md:w-[calc(58%-12.5px)]">
              <label
                className={`text-base font-medium ${
                  !duration ? "text-crimson" : "text-cloudBurst"
                }`}
              >
                Test Duration *
              </label>
              <div className="flex items-center gap-1">
                <div className={styles.select_wrapper} style={{ width: '150px', minWidth: '150px' }}>
                  <Select
                    fullWidth
                    MenuProps={{
                      classes: { root: styles.select_root },
                    }}
                    value={durationDropdownValue}
                    onChange={({ target: { value } }) => handleDurationDropdownChange(value)}
                    error={!duration}
                  >
                    {getTestDurationOptions().map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </div>

                {isCustomDuration && (
                  <div className="flex items-center gap-1 ml-2.5">
                    <InputField
                      isNumericField
                      error={showDurationError}
                      placeholder="0"
                      className="w-12"
                      value={customDurationInputs.minutes ? String(customDurationInputs.minutes) : ""}
                      onChange={({ target: { value } }) => handleCustomMinutesChange(value)}
                    />
                    <span className="text-xs text-cloudBurst whitespace-nowrap" style={{ marginRight: '5px' }}>min.</span>
                    <InputField
                      isNumericField
                      error={showDurationError}
                      placeholder="0"
                      className="w-12"
                      value={customDurationInputs.seconds ? String(customDurationInputs.seconds) : ""}
                      onChange={({ target: { value } }) => handleCustomSecondsChange(value)}
                    />
                    <span className="text-xs text-cloudBurst whitespace-nowrap">sec.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Frequency Column */}
            <div className="w-full md:w-[calc(42%-12.5px)]">
              <TestFrequencyInputs
                isTestSelected={selected}
                frequency={frequency}
                frequencyReps={frequencyReps}
                onChange={handleDataChange}
              />
            </div>
          </div>

          <div className="flex flex-col">
            {/* Headers Row - aligned with Duration/Frequency sections above */}
            <div className="flex flex-wrap gap-5 items-start pb-2 mb-1">
              <div className="flex flex-col w-full md:w-[calc(58%-12.5px)]">
                <p
                  className={`text-base font-medium ${
                    !tests.length ? "text-crimson" : "text-cloudBurst"
                  }`}
                >
                  Parameters *
                </p>
                {eyesNotSelected && (
                  <p className="text-crimson text-sm">
                    Please select at least one option
                  </p>
                )}
              </div>
              <div className="w-full md:w-[calc(42%-12.5px)] flex gap-4">
                <div className="flex-1 text-base font-medium text-cloudBurst text-center">
                  Eyes Open
                </div>
                <div className="flex-1 text-base font-medium text-cloudBurst text-center">
                  Eyes Closed
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              {/* Rows */}
              {feetOptions.map((option) => (
                <BalanceParameterInput
                  key={option}
                  feet={option}
                  tests={tests}
                  onChange={(value) => handleDataChange({ tests: value })}
                />
              ))}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default BalanceTest;
